import React from 'react'
import gql from 'graphql-tag'
import { useQuery } from 'react-apollo'
import { GraphQLFormProvider } from '@mymeerkat/react-form-helper'
import { isValidEmail, isValidRsaIdNumber } from '../../lib/validations'
import { BENEFICIARIES } from './Beneficiaries'
import { IdTypes, RELATIONSHIPS } from '../../variables'
import {
  buildPercentageFields,
  percentageInitialValues,
  cleanExistingBeneficiaries,
  PercentageSummary,
  UPDATE_FUNERAL_BENEFICIARY_PERCENTAGES,
} from './beneficiaryPercentages'

import { Card, renderInput, renderDisplay, BuildLayout, ActivityIndicator } from '../generic';

const FUNERAL_ACCOUNTING_IDENTIFIER = gql`
  query FuneralAccountingIdentifier($id: String!) {
    funeral(id: $id) {
      id
      accountingIdentifier
    }
  }`

const buildFields = (existingBeneficiaries = [], isOmasPolicy = false) => ({
  'beneficiaries[0].payTo.firstNames': {
    type: 'name',
    label: 'First Name *',
    validate: (value) => {
      if (!value) {
        return 'First name is Required'
      }
      if (value.length > 35) {
        return 'Length must be less than 35'
      }
    }
  },
  'beneficiaries[0].payTo.surname': {
    type: 'name',
    label: 'Last Name *',
    validate: (value) => {
      if (!value) {
        return 'Last name is required'
      }
      if (value.length > 35) {
        return 'Length must be less than 35'
      }
    }
  },
  'beneficiaries[0].payTo.idType': {
    type: 'select',
    label: 'ID Type*',
    nullOnly: true,
    options: IdTypes,
    validate: (value) => {
      if (!isOmasPolicy && !value) {
        return 'ID type is Required'
      }
    }
  },
  'beneficiaries[0].payTo.idNumber': {
    type: 'text',
    label: 'ID Number*',
    nullOnly: true,
    validate: (value, row) => {
      if (!isOmasPolicy && !value) {
        return 'ID type is Required'
      }
      else if (row.idType && row.idType === 'rsa_id' && isValidRsaIdNumber(value)) {
        return 'ID Number is invalid'
      }
    }
  },
  'beneficiaries[0].payTo.cellNumbers[0]': {
    type: 'text',
    label: 'Mobile Number*',
    validate: (value) => {
      if (!isOmasPolicy && !value) {
        return 'Mobile is Required'
      }
      if (value && value.length > 10) {
        return 'Phone numbers must be 10 long'
      }
    }
  },
  'beneficiaries[0].payTo.email': {
    type: 'email',
    label: 'Email*',
    validate: (value) => {
      if (value && isValidEmail(value)) {
        return 'Email not valid'
      }
    }
  },
  relationship: {
    label: "Relationship*",
    type: "select",
    options: RELATIONSHIPS.sort((a, b) => (a > b) ? 1 : -1).map(a => ({ value: a, label: a })),
    validate: (value) => {
      if (!value) {
        return 'Relationship is Required'
      }
    }
  },
  ...buildPercentageFields(existingBeneficiaries),
})

const config = {
  name: 'funeralbeneficiary',
  query: gql`
  query Member($id: String!) {
    funeral(id: $id) {
      id
      beneficiaries {
        id
        payTo {
          id
          firstNames
          surname
          idType
          idNumber
          cellNumbers
          clientId
        }
        bankAccount {
          id
          branchCode
          accountNo
        }
      }
    }
  }`,
  addMutation: gql`
  mutation AddFuneralBeneficiary($funeralId: ObjID!, $beneficiary: AddBeneficiaryInput) {
    addFuneralBeneficiary(funeralId:$funeralId, beneficiary: $beneficiary) {
      id
      beneficiaries {
      id
      payTo {
        id
        firstNames
        surname
        idType
        idNumber
        cellNumbers
        clientId
      }
      bankAccount {
        id
        branchCode
        accountNo
        }
      }
    }
  }`,
  updateMutation: gql`
    mutation UpdateBeneficiary($input: UpdateMemberInput!, $id: String!) {
      updateMember(id: $id, input: $input) {
        id
        beneficiaries {
        id
        payTo {
          id
          firstNames
          surname
          idType
          idNumber
          cellNumbers
          clientId
        }
        bankAccount {
          id
          branchCode
          accountNo
          }
        }
      }
  }`,
}

function FormLayout({ mode, cancel, data, formName, fields, existingBeneficiaries = [] }) {
  const { funeral } = data || {}
  const { beneficiaries } = funeral || {}
  const { payTo } = beneficiaries && beneficiaries.length > 0 ? beneficiaries[0] : {}
  const { clientId } = payTo || {}

  return (
    <Card>
      {mode === 'add' && <PercentageSummary existingBeneficiaries={existingBeneficiaries} />}
      <BuildLayout formName={formName} fieldNames={Object.keys(fields).filter(f => !f.hide)} fieldConfig={fields} mode={mode} direction='column' spacing={2} align='center' justify='center' cancel={cancel} allowNull={!clientId} />
    </Card>
  );
}

let NewBeneficiary = (props) => {
  const { id, funeralId, cancel, mode, existingBeneficiaries = [], apolloClient } = props
  const { data, loading, error } = useQuery(FUNERAL_ACCOUNTING_IDENTIFIER, { variables: { id: funeralId } })
  // OMAS beneficiaries do not need an ID type, ID number or mobile number
  const isOmasPolicy = data?.funeral?.accountingIdentifier === 'OMAS'
  const fields = buildFields(existingBeneficiaries, isOmasPolicy)

  if (loading) {
    return <ActivityIndicator />
  }

  return (
    <div>
      <GraphQLFormProvider
        id={id}
        {...config}
        fields={fields}
        mode={mode}
        initialValues={percentageInitialValues(existingBeneficiaries)}
        initialValuesFromData={(data) => data.funeral}
        renderDisplay={renderDisplay}
        renderInput={renderInput}
        InputFormLayout={(props) => <FormLayout formName={config.name} cancel={cancel} {...props} fields={fields} existingBeneficiaries={existingBeneficiaries} />}
        mapInputVariables={async (props) => {
          if (mode === 'edit') {
            const { beneficiaries, ...inputProps } = props
            const { id, payTo, bankAccount, ...rest } = beneficiaries[0]
            return { input: payTo }
          }
          else {
            const { beneficiaries, relationship, existingPercentages = [] } = props
            const { payTo, percentage } = beneficiaries[0]

            if (existingBeneficiaries.length > 0 && apolloClient) {
              await apolloClient.mutate({
                mutation: UPDATE_FUNERAL_BENEFICIARY_PERCENTAGES,
                variables: {
                  id,
                  input: { beneficiaries: cleanExistingBeneficiaries(existingBeneficiaries, existingPercentages) },
                },
              })
            }

            return {
              funeralId: id,
              beneficiary: { ...payTo, relationship, percentage: percentage != null ? percentage : 100 }
            }
          }
        }}
        afterSubmit={() => { cancel() }}
        refetchQueries={[{ query: BENEFICIARIES, variables: { id: funeralId } }]}
      />
    </div>
  )
}

export default NewBeneficiary
